/**
 * MY COLLATERAL (FR-U-CRD-006): what is pledged, to which loan, and whether it is still locked.
 *
 * The screen a borrower opens to answer one question: can I sell this? The answer is the position's
 * state, and it comes from the platform's own encumbrance ledger rather than being inferred from
 * whether a loan looks open. Those two can differ during enforcement, and the ledger is right.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type CollateralPosition } from '@/lib/api';
import { PageHeader } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { fmtDateShort } from '@/lib/format';
import { formatWireAmount } from '@/lib/money';

const STATE_NOTE: Record<string, string> = {
  pledged: 'Selected as collateral. The Trust has not verified it yet.',
  encumbered: 'Held by the Trust. It cannot be sold, listed, or pledged again.',
  under_enforcement: 'Under enforcement following a default.',
  realization_pending: 'Being realized to settle the loan.',
  realized: 'Realized. The proceeds were applied to the loan.',
  released: 'Released. These units are yours to use again.',
};

export default function CreditCollateral() {
  const [rows, setRows] = useState<CollateralPosition[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows((await api.creditMarket.myCollateral()).collateral);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your collateral.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!rows) return <LoadingCard lines={4} />;

  const live = rows.filter((r) => r.state !== 'released');
  const past = rows.filter((r) => r.state === 'released');

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageHeader title="Collateral" subtitle="What you have pledged, and what is still locked." />

      {rows.length === 0 ? (
        <EmptyState
          title="Nothing is pledged"
          body="When you borrow, the units you pledge appear here with their live lock status."
          action={{ label: 'Browse credit products', href: '/credit/borrow' }}
        />
      ) : (
        <>
          {live.length > 0 && <Group title="Currently pledged" rows={live} />}
          {past.length > 0 && <Group title="Released" rows={past} />}
        </>
      )}

      <p className="rounded-card bg-surface-sunken p-3 text-center text-xs leading-relaxed text-ink-muted">
        Pledged units cannot be sold on the marketplace or pledged to a second loan. Only the
        unencumbered part of a holding is available, and the platform enforces that on every attempt.
      </p>

      <Link
        to="/credit"
        className="inline-block rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink"
      >
        Back to Credit
      </Link>
    </div>
  );
}

function Group({ title, rows }: { title: string; rows: CollateralPosition[] }) {
  return (
    <section>
      <p className="mb-2 text-micro font-medium text-ink-muted">{title}</p>
      <ul className="space-y-2.5">
        {rows.map((r) => (
          <li key={r.id} className="rounded-card border border-border bg-surface p-4 shadow-card">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">{r.asset_name}</p>
                <p className="text-nano text-ink-subtle">
                  {(r.pledged_bps / 100).toFixed(2)}% of the asset · lien {r.lien_priority}
                  {r.request_reference && <> · {r.request_reference}</>}
                </p>
              </div>
              <StatusPill
                status={r.state}
                label={r.state === 'encumbered' ? 'Held by the Trust' : undefined}
              />
            </div>

            <p className="mt-2 text-micro leading-relaxed text-ink-muted">
              {STATE_NOTE[r.state] ?? r.state}
            </p>

            <dl className="mt-3 grid grid-cols-3 gap-3 border-t border-border pt-3">
              <Fact label="Valued at" value={`KES ${formatWireAmount(r.valuation_minor)}`} />
              <Fact label="Eligible" value={`KES ${formatWireAmount(r.eligible_minor)}`} />
              <Fact
                label={r.released_at ? 'Released' : 'Pledged'}
                value={fmtDateShort(r.released_at ?? r.pledged_at)}
              />
            </dl>

            {r.loan_id && (
              <Link
                to={`/credit/loans/${r.loan_id}`}
                className="mt-2.5 inline-block text-micro font-medium text-brand"
              >
                See the loan it secures &rarr;
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-nano text-ink-subtle">{label}</dt>
      <dd className="tabular truncate text-micro font-medium text-ink">{value}</dd>
    </div>
  );
}
